import { PunchStatus } from './enums';
/** 地球平均半径（米） */
const EARTH_RADIUS_M = 6_371_000;
const toRad = (deg) => (deg * Math.PI) / 180;
/**
 * 两点间大圆距离（米），Haversine 公式。
 *
 * 工地尺度（几百米到几公里）下精度远超 GPS 本身的误差，够用。
 */
export function distanceMeters(a, b) {
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
/** 定位精度差到这个程度就不自动判定，转人工复核（米） */
export const MAX_TRUSTED_ACCURACY_M = 200;
/** 设备时间与服务器时间允许的偏差，超过则标记（毫秒）。离线补传不适用此检查。 */
export const MAX_CLOCK_DRIFT_MS = 10 * 60 * 1000;
/**
 * 打卡地点判定。**只允许在服务端调用**——前端算的话，改前端代码就能伪造位置。
 * 前端可以用 distanceMeters 显示「距工地 xx 米」，但不能由前端决定是否通过。
 *
 * 判定顺序刻意如此：
 * 0. 拿不到定位 → 有理由就转人工复核，没理由则调用方负责拒掉（返回 null point
 *    而不填理由是调用方的 bug，不该在这里静默放过）。
 * 1. 模拟定位 → 转人工复核，不直接拒。部分机型省电模式会误报 isFromMockProvider，
 *    直接拒会误伤工人；交给管理员看一眼更稳妥。
 * 2. 定位精度太差 → 转人工复核。此时距离数字本身不可信，拒或过都是猜。
 * 3. 扣除精度误差后仍超出围栏 → 拒绝；但工人填了理由的转人工复核。
 *
 * **「继续打卡」的理由只能把「拒绝」抬到「待复核」，永远抬不到「通过」，
 * 也永远不会把「待复核」降下去。** 否则填个理由就能绕过围栏，围栏形同虚设。
 */
export function evaluateGeofence(input, config) {
    // 没有坐标：距离无从谈起，null 是唯一不撒谎的值。
    // 写 0 会让管理端显示「距工地 0 米」—— 看起来像站在工地正中央，完全误导。
    if (!input.point) {
        return {
            status: PunchStatus.PENDING_REVIEW,
            distanceM: null,
            effectiveDistanceM: null,
            reason: 'NO_LOCATION_OVERRIDE',
        };
    }
    const distanceM = distanceMeters(input.point, config.center);
    const accuracy = Math.max(0, input.accuracyM ?? 0);
    const effectiveDistanceM = Math.max(0, distanceM - accuracy);
    if (input.isMocked) {
        return {
            status: PunchStatus.PENDING_REVIEW,
            distanceM,
            effectiveDistanceM,
            reason: 'MOCK_LOCATION',
        };
    }
    if (accuracy > MAX_TRUSTED_ACCURACY_M) {
        return {
            status: PunchStatus.PENDING_REVIEW,
            distanceM,
            effectiveDistanceM,
            reason: 'ACCURACY_TOO_LOW',
        };
    }
    if (effectiveDistanceM > config.radiusM) {
        // 工人声明了原因（在围挡外作业、工地定位点不准……）就转人工复核，
        // 让管理员对着实际距离判断，而不是让他今天打不上卡
        return input.hasOverride
            ? {
                status: PunchStatus.PENDING_REVIEW,
                distanceM,
                effectiveDistanceM,
                reason: 'OUT_OF_RANGE_OVERRIDE',
            }
            : {
                status: PunchStatus.REJECTED,
                distanceM,
                effectiveDistanceM,
                reason: 'OUT_OF_RANGE',
            };
    }
    return {
        status: PunchStatus.APPROVED,
        distanceM,
        effectiveDistanceM,
        reason: 'IN_RANGE',
    };
}
/**
 * 从一组工地中挑出离当前位置最近的一个。
 * 用于工人同时属于多个工地时自动预选，最终仍由工人确认。
 */
export function nearestSite(point, sites) {
    let best = null;
    for (const site of sites) {
        const d = distanceMeters(point, { lat: site.lat, lng: site.lng });
        if (!best || d < best.distanceM)
            best = { site, distanceM: d };
    }
    return best;
}
//# sourceMappingURL=geo.js.map